import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Loader2, CalendarDays } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fDate } from "@/components/TicketSlide";
import { AnimatePresence, motion } from "framer-motion";
import { GiftEnvelope, Confetti } from "@/components/GiftEnvelope";
import { thanksWhatsAppUrl } from "@/lib/giftMessage";

function ThanksPanel({ info, token }) {
  const navigate = useNavigate();
  const thank = () => { api.post(`/gifts/${token}/thanked`).catch(() => {}); setTimeout(() => navigate("/mis-tickets"), 400); };
  return (
    <div className="space-y-2" data-testid="gift-thanks-panel">
      <p className="text-sm text-center font-semibold">¡Regalo recibido! Ya está en Mis Tickets.</p>
      <Button asChild className="w-full bg-[#25D366] hover:bg-[#1ebe5a] text-white">
        <a href={thanksWhatsAppUrl(info)} target="_blank" rel="noopener noreferrer" onClick={thank} data-testid="gift-thanks-whatsapp">Agradecer a {info.giver_name} por WhatsApp</a>
      </Button>
      <Button variant="outline" className="w-full" onClick={() => navigate("/mis-tickets")} data-testid="gift-go-tickets">Ver en Mis Tickets</Button>
    </div>
  );
}

const imgSrc = (s) => (!s ? null : s.startsWith("http") ? s : `${process.env.REACT_APP_BACKEND_URL}${s}`);

function GiftActions({ g, token }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [thanks, setThanks] = useState(null);
  const next = encodeURIComponent(`/regalo/${token}`);
  if (thanks) return <ThanksPanel info={thanks} token={token} />;
  if (g.status === "accepted") return <p className="text-sm font-medium text-center" data-testid="gift-claimed-note">Este regalo ya fue recibido.</p>;
  if (g.status !== "pending") return <p className="text-sm font-medium text-center" data-testid="gift-unavailable-note">Este regalo ya no est&aacute; disponible.</p>;
  if (!user) {
    return (
      <div className="space-y-2" data-testid="gift-auth-actions">
        <p className="text-sm text-center text-muted-foreground">Para recibir tu regalo, ingresa o crea tu cuenta en VIVOTaller.</p>
        <div className="grid grid-cols-2 gap-2">
          <Button asChild variant="outline"><Link to={`/login?next=${next}`} data-testid="gift-login-btn">Ingresar</Link></Button>
          <Button asChild><Link to={`/registro?next=${next}`} data-testid="gift-register-btn">Crear cuenta</Link></Button>
        </div>
      </div>
    );
  }
  if (user.role !== "customer") return <p className="text-sm text-center text-muted-foreground" data-testid="gift-role-note">Cambia a tu perfil de Alumno para recibir este regalo.</p>;
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/gifts/${token}/accept`);
      toast.success("¡Regalo recibido! Ya está en Mis Tickets");
      if (data.giver_phone) setThanks(data); else navigate("/mis-tickets");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  return <Button className="w-full" size="lg" onClick={accept} disabled={busy} data-testid="gift-accept-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Gift className="w-4 h-4 mr-2" />Recibir regalo</>}</Button>;
}

export default function GiftClaim() {
  const { token } = useParams();
  const [g, setG] = useState(undefined);
  const [opened, setOpened] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/gifts/${token}`).then((r) => { setG(r.data); if (r.data.status !== "pending") setOpened(true); }).catch(() => setG(null)); }, [token]);
  if (g === undefined) return <div className="py-24 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!g) return <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="gift-not-found"><Gift className="w-10 h-10 mx-auto mb-3 text-primary" /><h1 className="text-2xl font-extrabold">Regalo no encontrado</h1><p className="text-muted-foreground mt-2">Revisa que el enlace est&eacute; completo.</p></div>;
  return (
    <div className="max-w-md mx-auto px-4 py-10 relative [perspective:1200px]" data-testid="gift-claim-page">
      {opened && g.status === "pending" && <Confetti />}
      <AnimatePresence mode="wait">
      {!opened ? <GiftEnvelope key="env" name={g.recipient_name} onOpen={() => setOpened(true)} /> : (
      <motion.div key="card" initial={{ opacity: 0, y: 30, rotateX: -25 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ type: "spring", stiffness: 140, damping: 18 }}>
      <Card className="overflow-hidden" data-testid="gift-card-open">
        {imgSrc(g.image) && <img src={imgSrc(g.image)} alt={g.workshop_name} className="w-full h-44 object-cover" />}
        <div className="p-6 space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary grid place-items-center mx-auto"><Gift className="w-7 h-7" /></div>
          <h1 className="text-2xl font-extrabold text-center" data-testid="gift-title">{g.recipient_name}, tienes un regalo</h1>
          <p className="text-center text-muted-foreground"><strong className="text-foreground">{g.giver_name}</strong> te regal&oacute; el taller <Link to={`/taller/${g.workshop_id}`} className="text-primary font-semibold hover:underline" data-testid="gift-workshop-link">{g.workshop_name}</Link>.</p>
          {g.message && <blockquote className="rounded-2xl bg-secondary p-4 text-center italic" data-testid="gift-message-text">&ldquo;{g.message}&rdquo;</blockquote>}
          {g.starts_at && <p className="text-sm text-center flex items-center justify-center gap-1.5 text-muted-foreground"><CalendarDays className="w-4 h-4" />Comienza el {fDate(g.starts_at)}</p>}
          <GiftActions g={g} token={token} />
        </div>
      </Card>
      </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}
